import Cookies from 'js-cookie';
import { Loader2, Send } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

// ============================================================================
// Types
// ============================================================================

interface EmailPreviewResponse {
  subject: string;
  body: string;
  step_id: string;
  step_label: string;
  recipient_info: string | null;
}

export interface EmailPreviewModalProps {
  open: boolean;
  onClose: () => void;
  campaignId: string;
  stepId?: string;
  onEmailSent?: () => void;
}

// ============================================================================
// Component
// ============================================================================

export const EmailPreviewModal: React.FC<EmailPreviewModalProps> = ({
  open,
  onClose,
  campaignId,
  stepId,
  onEmailSent,
}) => {
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [stepLabel, setStepLabel] = useState('');
  const [recipientInfo, setRecipientInfo] = useState<string | null>(null);

  // Fetch email preview when modal opens
  const fetchPreview = useCallback(async () => {
    setLoading(true);
    try {
      const url = new URL(
        `${import.meta.env.VITE_APP_DJANGO_BACKEND_HOST}/api/loa-outreach/${campaignId}/preview-email/`,
      );
      if (stepId) {
        url.searchParams.set('step_id', stepId);
      }

      const res = await fetch(url.toString(), { credentials: 'include' });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(
          errorData.error || `Failed to load preview (${res.status})`,
        );
      }

      const data: EmailPreviewResponse = await res.json();
      setSubject(data.subject);
      setBody(data.body);
      setStepLabel(data.step_label);
      setRecipientInfo(data.recipient_info);
    } catch (e) {
      const message =
        e instanceof Error ? e.message : 'Failed to load email preview';
      toast.error(message);
      onClose();
    } finally {
      setLoading(false);
    }
  }, [campaignId, stepId, onClose]);

  useEffect(() => {
    if (open) {
      fetchPreview();
    }
  }, [open, fetchPreview]);

  const handleSend = async () => {
    setSending(true);
    try {
      const res = await fetch(
        `${import.meta.env.VITE_APP_DJANGO_BACKEND_HOST}/api/loa-outreach/${campaignId}/send-custom-email/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': Cookies.get('csrftoken') || '',
          },
          credentials: 'include',
          body: JSON.stringify({ subject, body }),
        },
      );

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(
          errorData.error || `Failed to send email (${res.status})`,
        );
      }

      toast.success('Email sent successfully');
      onEmailSent?.();
      onClose();
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to send email';
      toast.error(message);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-sm font-semibold">
            Edit Follow-up Email
          </DialogTitle>
          {stepLabel && (
            <p className="text-[11px] text-zinc-600 font-medium mt-0.5">
              {stepLabel}
              {recipientInfo && (
                <span className="text-zinc-400"> · To: {recipientInfo}</span>
              )}
            </p>
          )}
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-violet-500" />
            <span className="ml-2 text-sm text-zinc-600">
              Loading email preview...
            </span>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto space-y-4 py-2">
            {/* Subject */}
            <div className="space-y-1.5">
              <Label
                htmlFor="email-subject"
                className="text-xs font-medium text-zinc-700"
              >
                Subject
              </Label>
              <Input
                id="email-subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="text-sm"
                placeholder="Email subject..."
              />
            </div>

            {/* Body */}
            <div className="space-y-1.5 flex-1">
              <Label
                htmlFor="email-body"
                className="text-xs font-medium text-zinc-700"
              >
                Message
              </Label>
              <textarea
                id="email-body"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                className="w-full min-h-[300px] px-3 py-2 text-sm border border-zinc-200 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-y"
                placeholder="Email body..."
              />
              <p className="text-[10px] text-zinc-500">
                You can use HTML formatting. The email will be sent as a reply
                to the existing thread.
              </p>
            </div>
          </div>
        )}

        <DialogFooter className="pt-4 border-t border-zinc-100">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={sending}
            className="text-xs"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={loading || sending || !subject.trim() || !body.trim()}
            className="text-xs bg-violet-600 hover:bg-violet-700"
          >
            {sending ? (
              <>
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="h-3.5 w-3.5 mr-1.5" />
                Send Email
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EmailPreviewModal;
